import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Field, FieldProps, getIn, useFormikContext } from "formik";

import FocusError from "./components/FocusError";
import "../../forms-common.scss";
import useThemeService from "../../services/theme-service/ThemeService";
import { translationService } from "../../services/translation/TranslationService";
import { ValidationFunction } from "../../services/form/validators/FormValidators";
import { Validator } from "../../services/form/validators/Validator";
import { ValidationMesssageDetails } from "../../services/form/validators/ValidationMesssage";
import { ErrorMessageWithContext } from "../../services/api/BadRequestError";

export interface FormFieldProps {
  id?: string;
  className?: string;
  labelKey: string;
  labelIcon?: React.ReactElement | undefined;
  fieldName?: string;
  fieldValue: any;
  fieldType?: string;
  component?: string | React.ComponentType<FieldProps<any>> | React.ComponentType<void>;
  validate?: ValidationFunction;
  validator?: Validator;
  placeholderKey?: string;
  maxLength?: number;
  children?: React.ReactNode | ((props: FieldProps) => React.ReactNode);
  errorName?: string;
  readOnly?: boolean;
  required?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  accessoryElement?: JSX.Element;
  tooltip?: string;
}

export interface FormFieldErrorProps {
  errorName: string;
}

const Error = observer(function (props: FormFieldErrorProps) {
  const { errors } = useFormikContext();
  const error = getIn(errors, props.errorName);

  const getErrorMessageDetails = useCallback((errorMessage: ValidationMesssageDetails) => {
    return (
      <label>
        {translationService.translate(errorMessage.mainKey)}
        <ul className="error-message-details">
          {errorMessage.detailsKey.map((message: any) => (
            <li>{translationService.translate(message)}</li>
          ))}
        </ul>
      </label>
    );
  }, []);

  const getErrorMessage = useCallback(
    (error: any) => {
      if (error && error.message && error.message.detailsKey.length > 0)
        return getErrorMessageDetails(error.message);

      if (error && error.message)
        return translationService.translate(error.message.mainKey, error.context);

      if (apiValidationError(error)) return getTranslatedApiValidationError(error);

      return translationService.translate(error);
    },
    [getErrorMessageDetails]
  );
  return <div className="message">{getErrorMessage(error)}</div>;
});

export default observer(function FormField(props: FormFieldProps) {
  const { themeClass } = useThemeService();

  const fieldLabel = (
    <div className={themeClass([props.readOnly ? "label" : "label-edit"])}>
      <label title={props.tooltip || ""}>
        {`${translationService.translate(props.labelKey)} ${props.required ? "*" : ""}`}
      </label>
      {props.labelIcon && props.labelIcon}
    </div>
  );

  const field = (
    <>
      <Field
        id={props.id}
        className={props.className ? themeClass([props.className]) : ""}
        type={props.fieldType}
        name={props.fieldName}
        value={props.fieldValue}
        component={props.component}
        validate={
          props.disabled || (!props.required && !props.fieldValue) ? {} : props.validator?.validate
        }
        placeholder={props.placeholderKey ? translationService.translate(props.placeholderKey) : ""}
        maxLength={props.maxLength}
        autoComplete={props.autoComplete}
        disabled={props.disabled}
      >
        {props.children}
      </Field>

      <FocusError />
      {props.errorName && (
        <div className="error-message">
          <Error errorName={props.errorName} />
        </div>
      )}
    </>
  );

  const readOnlyField = (
    <span id={props.id} className="read-only">
      {props.fieldValue}
    </span>
  );

  return (
    <div className="field" id={props.id && `${props.id}-field`}>
      {fieldLabel}
      <div className="form-element-container">
        {props.readOnly ? readOnlyField : field}
        {props.accessoryElement}
      </div>
    </div>
  );
});

function apiValidationError(error: any) {
  return (
    error &&
    typeof error === "string" &&
    error.includes('"MessageKey":') &&
    error.includes('"Context":')
  );
}

function getTranslatedApiValidationError(error: string) {
  let fieldError = error.replaceAll('^"|"$', "");
  var jsonParsedError = JSON.parse(fieldError);
  var errorMessageAndContext: ErrorMessageWithContext = {
    MessageKey: jsonParsedError.MessageKey,
    Context: jsonParsedError.Context,
  };
  return translateComplexValidation(errorMessageAndContext);
}

function translateComplexValidation(errorMessageAndContext: ErrorMessageWithContext) {
  if (errorMessageAndContext.Context) return translateErrorWithContext(errorMessageAndContext);
  else return translationService.translate(errorMessageAndContext.MessageKey);
}

function translateErrorWithContext(errorMessageAndContext: ErrorMessageWithContext): string {
  if (
    typeof errorMessageAndContext.Context === "string" ||
    typeof errorMessageAndContext.Context === "number"
  ) {
    const context = { context: errorMessageAndContext.Context };
    return translationService.translate(errorMessageAndContext.MessageKey, context);
  } else if (Object.keys(errorMessageAndContext.Context).length > 0)
    return translationService.translate(
      errorMessageAndContext.MessageKey,
      errorMessageAndContext.Context
    );

  return translationService.translate(errorMessageAndContext.MessageKey);
}
